/**
 * Display units (launch-readiness Page 17, decision S4). The backend keeps its
 * stored units (metres for heights, distances and accuracy, m/s for speed,
 * square feet for facility area, kilograms, degrees Celsius, and the
 * facility's own scale_unit for floor-plan calibration); the UI converts what
 * it shows and what is typed in, in this one module. Every function takes the
 * stored value and the effective measurement system (useMeasurementSystem).
 */

/** A display unit system: organizations.measurement_system / users.measurement_system_override. */
export type MeasurementSystem = 'metric' | 'imperial';

/** Exact conversion factors (international yard and pound, 1959). */
export const METERS_PER_FOOT = 0.3048;
export const METERS_PER_MILE = 1609.344;
export const SQ_METERS_PER_SQ_FOOT = METERS_PER_FOOT * METERS_PER_FOOT;
export const KG_PER_POUND = 0.45359237;
export const MPS_PER_MPH = METERS_PER_MILE / 3600;

/** Normalizes any value to a measurement system; unknown values are metric (the column default). */
export function asMeasurementSystem(value: unknown): MeasurementSystem {
  return value === 'imperial' ? 'imperial' : 'metric';
}

function fixed(value: number, precision: number): string {
  // Avoid "-0.0" for a tiny negative residue.
  const rounded = Number(value.toFixed(precision));
  return (Object.is(rounded, -0) ? 0 : rounded).toFixed(precision);
}

// ── Inputs ───────────────────────────────────────────────────────────────────

/** A display-unit number as input text: at most `precision` decimals, no trailing zeros. */
export function inputNumberText(value: number, precision = 2): string {
  if (!Number.isFinite(value)) return '';
  const rounded = Number(value.toFixed(precision));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

/**
 * The stored value for an edited input. `shownText` is what the input showed
 * for `stored`; when the text is unchanged the stored value comes back
 * exactly, so saving without edits never drifts by display rounding. Blank
 * text is undefined; anything else is converted with `toStored`.
 */
export function storedFromInput(
  text: string,
  shownText: string,
  stored: number | null | undefined,
  toStored: (displayValue: number) => number,
): number | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  if (trimmed === shownText.trim() && typeof stored === 'number' && Number.isFinite(stored)) return stored;
  const n = Number(trimmed);
  return Number.isFinite(n) ? toStored(n) : undefined;
}

// ── Length (stored in metres) ────────────────────────────────────────────────

/** Short unit label for lengths: m or ft. */
export function lengthUnit(system: MeasurementSystem): 'm' | 'ft' {
  return system === 'imperial' ? 'ft' : 'm';
}

/** Metres to the display unit (m or ft). */
export function metersToDisplayLength(meters: number, system: MeasurementSystem): number {
  return system === 'imperial' ? meters / METERS_PER_FOOT : meters;
}

/** A typed length in the display unit back to metres (for saving). */
export function displayLengthToMeters(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value * METERS_PER_FOOT : value;
}

/** "1.5 m" / "4.9 ft" from metres. */
export function formatLength(meters: number, system: MeasurementSystem, precision = 1): string {
  return `${fixed(metersToDisplayLength(meters, system), precision)} ${lengthUnit(system)}`;
}

/** Unit label for long distances: km or mi. */
export function longDistanceUnit(system: MeasurementSystem): 'km' | 'mi' {
  return system === 'imperial' ? 'mi' : 'km';
}

/** Metres to kilometres or miles. */
export function metersToLongDistance(meters: number, system: MeasurementSystem): number {
  return system === 'imperial' ? meters / METERS_PER_MILE : meters / 1000;
}

/** "1.23 km" / "0.76 mi" from metres. */
export function formatLongDistance(meters: number, system: MeasurementSystem, precision = 2): string {
  return `${fixed(metersToLongDistance(meters, system), precision)} ${longDistanceUnit(system)}`;
}

// ── Area (facilities.total_area_sqft is stored in square feet) ───────────────

/** Unit label for areas: m² or sq ft. */
export function areaUnit(system: MeasurementSystem): 'm²' | 'sq ft' {
  return system === 'imperial' ? 'sq ft' : 'm²';
}

/** Square feet (as stored) to the display unit. */
export function sqFtToDisplayArea(sqFt: number, system: MeasurementSystem): number {
  return system === 'imperial' ? sqFt : sqFt * SQ_METERS_PER_SQ_FOOT;
}

/** A typed area in the display unit back to square feet (for saving). */
export function displayAreaToSqFt(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value : value / SQ_METERS_PER_SQ_FOOT;
}

/** "929 m²" / "10,000 sq ft" from square feet. */
export function formatAreaFromSqFt(sqFt: number, system: MeasurementSystem, precision = 0): string {
  const value = sqFtToDisplayArea(sqFt, system);
  return `${value.toLocaleString('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision })} ${areaUnit(system)}`;
}

// ── Speed (stored in metres per second) ──────────────────────────────────────

/** Unit label for speeds: m/s or mph. */
export function speedUnit(system: MeasurementSystem): 'm/s' | 'mph' {
  return system === 'imperial' ? 'mph' : 'm/s';
}

/** m/s to the display unit. */
export function mpsToDisplaySpeed(mps: number, system: MeasurementSystem): number {
  return system === 'imperial' ? mps / MPS_PER_MPH : mps;
}

/** A typed speed in the display unit back to m/s (for saving). */
export function displaySpeedToMps(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value * MPS_PER_MPH : value;
}

/** "3.0 m/s" / "6.7 mph" from m/s. */
export function formatSpeed(mps: number, system: MeasurementSystem, precision = 1): string {
  return `${fixed(mpsToDisplaySpeed(mps, system), precision)} ${speedUnit(system)}`;
}

// ── Weight (stored in kilograms) ─────────────────────────────────────────────

/** Unit label for weights: kg or lb. */
export function weightUnit(system: MeasurementSystem): 'kg' | 'lb' {
  return system === 'imperial' ? 'lb' : 'kg';
}

/** Kilograms to the display unit. */
export function kgToDisplayWeight(kg: number, system: MeasurementSystem): number {
  return system === 'imperial' ? kg / KG_PER_POUND : kg;
}

/** A typed weight in the display unit back to kilograms (for saving). */
export function displayWeightToKg(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value * KG_PER_POUND : value;
}

/** "23.0 kg" / "50.7 lb" from kilograms. */
export function formatWeight(kg: number, system: MeasurementSystem, precision = 1): string {
  return `${fixed(kgToDisplayWeight(kg, system), precision)} ${weightUnit(system)}`;
}

// ── Temperature (stored in degrees Celsius) ──────────────────────────────────

/** Unit label for temperatures. */
export function temperatureUnit(system: MeasurementSystem): '°C' | '°F' {
  return system === 'imperial' ? '°F' : '°C';
}

/** Celsius to the display unit. */
export function celsiusToDisplayTemperature(celsius: number, system: MeasurementSystem): number {
  return system === 'imperial' ? celsius * 9 / 5 + 32 : celsius;
}

/** A typed temperature in the display unit back to Celsius (for saving). */
export function displayTemperatureToCelsius(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? (value - 32) * 5 / 9 : value;
}

/** "21.0 °C" / "69.8 °F" from Celsius. */
export function formatTemperature(celsius: number, system: MeasurementSystem, precision = 1): string {
  return `${fixed(celsiusToDisplayTemperature(celsius, system), precision)} ${temperatureUnit(system)}`;
}

// ── Floor-plan scale units (facilities.scale_unit) ───────────────────────────

/**
 * Metres per one facilities.scale_unit (feet for current facilities), or null
 * for a unit this module does not know. Mirrors metresPerScaleUnit in
 * backend/src/services/facilityExport.ts.
 */
export function metersPerScaleUnit(unit: string | null | undefined): number | null {
  const u = (unit ?? 'feet').trim().toLowerCase();
  if (['feet', 'foot', 'ft'].includes(u)) return METERS_PER_FOOT;
  if (['meters', 'metres', 'meter', 'metre', 'm'].includes(u)) return 1;
  if (['inches', 'inch', 'in'].includes(u)) return 0.0254;
  if (['yards', 'yard', 'yd'].includes(u)) return 0.9144;
  if (['centimeters', 'centimetres', 'centimeter', 'centimetre', 'cm'].includes(u)) return 0.01;
  return null;
}

/**
 * A length in the facility's scale_unit to the display unit (m or ft). When
 * the scale unit is unknown the value is returned unconverted with
 * `converted: false`, so the caller keeps the original unit label.
 */
export function scaleLengthToDisplay(
  value: number,
  scaleUnit: string | null | undefined,
  system: MeasurementSystem,
): { value: number; unit: string; converted: boolean } {
  const perUnit = metersPerScaleUnit(scaleUnit);
  if (perUnit === null) return { value, unit: scaleUnit ?? 'units', converted: false };
  return { value: metersToDisplayLength(value * perUnit, system), unit: lengthUnit(system), converted: true };
}

/** A typed length in the display unit back to the facility's scale_unit (null when the unit is unknown). */
export function displayLengthToScaleUnit(
  value: number,
  scaleUnit: string | null | undefined,
  system: MeasurementSystem,
): number | null {
  const perUnit = metersPerScaleUnit(scaleUnit);
  if (perUnit === null) return null;
  return displayLengthToMeters(value, system) / perUnit;
}

/** "12.0 ft" / "3.7 m" for a length stored in the facility's scale_unit. */
export function formatScaleLength(
  value: number,
  scaleUnit: string | null | undefined,
  system: MeasurementSystem,
  precision = 1,
): string {
  const d = scaleLengthToDisplay(value, scaleUnit, system);
  return `${fixed(d.value, precision)} ${d.unit}`;
}

/**
 * Pixels per facility scale_unit to pixels per display unit ("px/m" or
 * "px/ft"), for showing a floor-plan scale. Unknown units stay as they are.
 */
export function pixelsPerDisplayUnit(
  pixelsPerScaleUnit: number,
  scaleUnit: string | null | undefined,
  system: MeasurementSystem,
): { value: number; unit: string } {
  const perUnit = metersPerScaleUnit(scaleUnit);
  if (perUnit === null) return { value: pixelsPerScaleUnit, unit: scaleUnit ?? 'unit' };
  const pxPerMeter = pixelsPerScaleUnit / perUnit;
  return system === 'imperial'
    ? { value: pxPerMeter * METERS_PER_FOOT, unit: 'ft' }
    : { value: pxPerMeter, unit: 'm' };
}
